/**
 * GeneralObservationsPanel — Renders the active "General Observations" sub-panel.
 *
 * Two panel types:
 *   • self_reported — clinician notes only (CAT-Q / General Questions probing
 *     panel removed for a simpler live-appointment workflow).
 *   • sensory — DSM-5 Criterion B4 (Hyper-/hyporeactivity to sensory input).
 *     Probing questions grouped by 8 sensory domains (auditory, visual, tactile
 *     etc.), plus an evidence decision selector (Clear / Partial / No / Inconclusive)
 *     that feeds into the B4 row of the Observation Review summary table.
 *
 * Data is stored in allTaskData under a string key (e.g. 'general_self_reported')
 * rather than a numeric task ID, using the same onFieldChange(fieldId, value)
 * callback that the rest of the observation module uses.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronDownIcon, XIcon, SearchIcon, ExclamationIcon } from '@heroicons/react/solid';
import StructuredNotesEditor, { PROBING_Q_MIME } from './StructuredNotesEditor';
import ObsTaggedNotesPanel from './ObsTaggedNotesPanel';
import InfoTooltip from '../../../../components/InfoTooltip';
import { DSM5_DOMAINS, DSM5_EVIDENCE_OPTIONS } from '../config/observationTasksConfig';
import { SENSORY_CATEGORIES } from '../config/generalObservationsConfig';
import { OBS_NOTE_TAG_LABEL_BY_ID } from '../config/obsNoteTagOptions';

/** How long the untag exit animation runs before we drop the note from the UI. */
const UNTAG_EXIT_MS = 300;

// ─────────────────────────────────────────────────────────────────────────
//  CollapsibleQuestionGroup — reusable accordion for probing question lists
//
//  Used by the Sensory panel. Questions are plain strings (no numbering).
//  Same click-to-mark + drag-into-notes interaction as the SSI module.
// ─────────────────────────────────────────────────────────────────────────

function CollapsibleQuestionGroup({ group, isExpanded, onToggle, discussedSet, onToggleDiscussed }) {
  const contentRef = useRef(null);
  const innerRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;
    const ro = new ResizeObserver(() => setMeasuredHeight(inner.scrollHeight));
    ro.observe(inner);
    setMeasuredHeight(inner.scrollHeight);
    return () => ro.disconnect();
  }, [group]);

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-150 text-left"
      >
        <ChevronDownIcon
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
            isExpanded ? 'rotate-180' : 'rotate-0'
          }`}
        />
        <span className="text-sm font-semibold text-slate-700">{group.title}</span>
        <span className="text-[10px] text-gray-400 ml-auto">{group.questions.length}</span>
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300"
        style={{
          maxHeight: isExpanded ? `${measuredHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
          transitionTimingFunction: isExpanded
            ? 'cubic-bezier(0.4, 0, 0.2, 1)'
            : 'cubic-bezier(0.4, 0, 1, 1)',
        }}
      >
        <div ref={innerRef} className="ml-6 mt-1 mb-2">
          <div className="flex flex-col gap-1.5">
            {group.questions.map((q, idx) => {
              const isDiscussed = discussedSet.has(q);
              return (
                <div
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    e.dataTransfer.setData(PROBING_Q_MIME, q);
                    e.dataTransfer.setData('text/plain', q);
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                  onClick={() => onToggleDiscussed(q)}
                  className={`
                    px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                    cursor-pointer select-none transition-all duration-200
                    ${isDiscussed
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-800 hover:bg-blue-50/60'}
                  `}
                  title={isDiscussed ? 'Click to unmark' : 'Click to mark as discussed — or drag into notes'}
                >
                  <div className="flex items-start gap-2.5">
                    <span className="text-sm leading-relaxed flex-shrink-0 text-gray-400">•</span>
                    <span>&ldquo;{q}&rdquo;</span>
                  </div>

                  <div
                    className="grid transition-all duration-300 ease-in-out"
                    style={{
                      gridTemplateRows: isDiscussed ? '1fr' : '0fr',
                      opacity: isDiscussed ? 1 : 0,
                      marginTop: isDiscussed ? '0.25rem' : '0px',
                    }}
                  >
                    <div className="overflow-hidden">
                      <span className="text-[10px] font-semibold text-blue-500 pl-5 not-italic block pb-0.5">
                        Marked as discussed
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Evidence button colour scheme — same traffic-light metaphor as the SSI
//  and Observation task panels so clinicians see a consistent visual language.
// ─────────────────────────────────────────────────────────────────────────

const EVIDENCE_COLOURS = {
  'No evidence':      { selectedBg: 'bg-emerald-600', selectedBorder: 'border-emerald-600', hoverBorder: 'hover:border-emerald-400', text: 'text-emerald-700' },
  'Partial evidence': { selectedBg: 'bg-amber-500',   selectedBorder: 'border-amber-500',   hoverBorder: 'hover:border-amber-400',   text: 'text-amber-700' },
  'Clear evidence':   { selectedBg: 'bg-red-500',     selectedBorder: 'border-red-500',     hoverBorder: 'hover:border-red-400',     text: 'text-red-700' },
  'Inconclusive':     { selectedBg: 'bg-gray-500',    selectedBorder: 'border-gray-500',    hoverBorder: 'hover:border-gray-400',    text: 'text-gray-600' },
};

// ─────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────

export default function GeneralObservationsPanel({
  panelId,
  data,
  onFieldChange,
  moveTargets = null,
  onMoveNote = null,
  readOnly = false,
  taggingEnabled = false,
  /**
   * v2 only: notes tagged `self_reported` that still live in Task Admin /
   * ADOS arrays. Shown above the editable Self-Report box so clinicians can
   * see (and re-tag) everything that will feed the Self-Report paragraph.
   */
  externalTaggedTaskNotes = null,
  externalTaggedAdosNotes = null,
  onUpdateNoteTags = null,
}) {
  // Global id of this panel's notes box, used by the cross-page Move picker.
  const currentMoveId = `general:${panelId}:notes`;
  if (panelId === 'sensory') {
    return <SensoryPanel data={data} onFieldChange={onFieldChange} readOnly={readOnly} moveTargets={moveTargets} onMoveNote={onMoveNote} currentMoveId={currentMoveId} taggingEnabled={taggingEnabled} />;
  }
  return (
    <SelfReportedPanel
      data={data}
      onFieldChange={onFieldChange}
      readOnly={readOnly}
      moveTargets={moveTargets}
      onMoveNote={onMoveNote}
      currentMoveId={currentMoveId}
      taggingEnabled={taggingEnabled}
      externalTaggedTaskNotes={externalTaggedTaskNotes}
      externalTaggedAdosNotes={externalTaggedAdosNotes}
      onUpdateNoteTags={onUpdateNoteTags}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  SelfReportedPanel — CAT-Q statements + notes
// ─────────────────────────────────────────────────────────────────────────

function SelfReportedPanel({
  data,
  onFieldChange,
  readOnly = false,
  moveTargets = null,
  onMoveNote = null,
  currentMoveId = null,
  taggingEnabled = false,
  externalTaggedTaskNotes = null,
  externalTaggedAdosNotes = null,
  onUpdateNoteTags = null,
}) {
  const val = useCallback(
    (fieldId) => (data || {})[fieldId],
    [data],
  );

  // Self-Report is notes-only: clinicians capture free-text observations here
  // without the CAT-Q / General Questions probing panel (removed for a simpler
  // live-appointment workflow). Any legacy asked_questions / asked_general
  // fields in Firestore are preserved but no longer surfaced in the UI.
  const notes = useMemo(() => {
    const raw = val('notes');
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) return [{ id: Date.now(), text: raw.trim(), type: 'note' }];
    return [];
  }, [val]);

  const handleNotesChange = useCallback(
    (newNotes) => { if (!readOnly) onFieldChange('notes', newNotes); },
    [onFieldChange, readOnly],
  );

  // ── Untag-from-Self-Report exit animation (cross-tagged notes only) ────
  // Notes typed in the editable box below live here permanently; these panels
  // only show notes that still live in Task Admin / ADOS and happen to carry
  // the self_reported tag. Removing that tag makes them leave this page.
  const [pendingUntag, setPendingUntag] = useState(null);
  const [exitingSnapshots, setExitingSnapshots] = useState([]);
  const [exitingNoteIds, setExitingNoteIds] = useState(() => new Set());

  const canEdit = !readOnly && taggingEnabled;
  const taskNotes = Array.isArray(externalTaggedTaskNotes) ? externalTaggedTaskNotes : [];
  const adosNotes = Array.isArray(externalTaggedAdosNotes) ? externalTaggedAdosNotes : [];
  // Always show the two source panels in v2 (even when empty) so clinicians
  // know where cross-tagged SR notes will land — same pattern as Criterion
  // Mapping's ADOS / Task Admin columns.
  const showExternalTagged = taggingEnabled;

  const handleRequestRemoveProtectedTag = useCallback((payload) => {
    setPendingUntag(payload);
  }, []);

  const confirmUntag = useCallback(() => {
    if (!pendingUntag || !onUpdateNoteTags) {
      setPendingUntag(null);
      return;
    }
    const { note, nextTags } = pendingUntag;
    setPendingUntag(null);

    setExitingSnapshots((prev) => (
      prev.some((n) => n.id === note.id) ? prev : [...prev, note]
    ));

    requestAnimationFrame(() => {
      setExitingNoteIds((prev) => {
        const next = new Set(prev);
        next.add(note.id);
        return next;
      });
      onUpdateNoteTags(note.sourceRef, note.id, nextTags);
    });

    window.setTimeout(() => {
      setExitingSnapshots((prev) => prev.filter((n) => n.id !== note.id));
      setExitingNoteIds((prev) => {
        const next = new Set(prev);
        next.delete(note.id);
        return next;
      });
    }, UNTAG_EXIT_MS);
  }, [pendingUntag, onUpdateNoteTags]);

  const protectedLabel = OBS_NOTE_TAG_LABEL_BY_ID.self_reported || 'Self-Reported Behaviours';

  return (
    <div>
      <h1 className="text-gray-900 text-3xl font-bold text-left mb-6">Self-Report</h1>
      {taggingEnabled && (
        <p className="text-sm text-gray-500 text-left mb-5 max-w-3xl">
          Notes you type here are auto-tagged as Self-Reported Behaviours (SR).
          Notes tagged SR on Task Administration or ADOS-informed Coding also
          appear below so you can review everything that feeds the Self-Report
          paragraph.
        </p>
      )}

      {/* Cross-tagged notes — still stored on their Task / ADOS source pages.
          Shown here so the Self-Report page is the single review surface for
          everything tagged SR (mirrors Criterion Mapping's tagged panels). */}
      {showExternalTagged && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch mb-5 max-w-6xl">
          <ObsTaggedNotesPanel
            title="From Task Administration"
            subtitle="Notes tagged Self-Reported Behaviours on a task — Tag to reassign"
            notes={taskNotes}
            emptyMessage="No task notes tagged as Self-Reported Behaviours yet."
            pageId="self_reported"
            canEdit={canEdit}
            onUpdateNoteTags={onUpdateNoteTags}
            exitingNoteIds={exitingNoteIds}
            exitingSnapshots={exitingSnapshots.filter((n) => n.sourceKind === 'task')}
            onRequestRemoveProtectedTag={handleRequestRemoveProtectedTag}
            minHeight="8rem"
          />
          <ObsTaggedNotesPanel
            title="From ADOS-informed Coding"
            subtitle="Notes tagged Self-Reported Behaviours on an ADOS item — Tag to reassign"
            notes={adosNotes}
            emptyMessage="No ADOS notes tagged as Self-Reported Behaviours yet."
            pageId="self_reported"
            canEdit={canEdit}
            onUpdateNoteTags={onUpdateNoteTags}
            exitingNoteIds={exitingNoteIds}
            exitingSnapshots={exitingSnapshots.filter((n) => n.sourceKind === 'ados')}
            onRequestRemoveProtectedTag={handleRequestRemoveProtectedTag}
            minHeight="8rem"
          />
        </div>
      )}

      <div className="max-w-3xl text-left">
        {/* overflow-visible on the notes body so the Tag / Move picker popover
            is not clipped by the card chrome (the header keeps rounded top
            corners). */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="bg-primary px-4 py-3 rounded-t-xl">
            <h3 className="text-sm font-bold text-white">
              {taggingEnabled ? 'Self-Report notes' : 'Notes'}
            </h3>
            {taggingEnabled && (
              <p className="text-xs text-white/80 mt-0.5">
                Typed here — auto-tagged SR
              </p>
            )}
          </div>
          <div className="bg-gray-50 px-4 py-4 rounded-b-xl overflow-visible">
            <StructuredNotesEditor
              notes={notes}
              onChange={readOnly ? undefined : handleNotesChange}
              placeholder="Type self-reported observations here…"
              readOnlyPlaceholder="No notes recorded"
              readOnly={readOnly}
              rows={8}
              moveTargets={moveTargets}
              currentMoveId={currentMoveId}
              onMoveNoteTo={readOnly ? undefined : onMoveNote}
              // v2: Tag picker + auto-tag every newly committed note as
              // self_reported (same pattern as ADOS item defaultTags). v1
              // patients keep plain notes with no criteriaTags.
              enableCriteriaTagging={taggingEnabled && !readOnly}
              defaultCriteriaTags={taggingEnabled ? ['self_reported'] : null}
            />
          </div>
        </div>
      </div>

      {pendingUntag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setPendingUntag(null)}
          />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="sr-untag-confirm-title"
            className="relative bg-white rounded-xl shadow-2xl p-6 w-[480px] max-w-[90vw] text-left"
          >
            <button
              type="button"
              onClick={() => setPendingUntag(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl leading-none"
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex items-center gap-2 mb-3">
              <ExclamationIcon className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <h2 id="sr-untag-confirm-title" className="text-lg font-bold text-gray-900">
                Remove Self-Report tag?
              </h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              This note will no longer appear under{' '}
              <span className="font-semibold text-gray-800">{protectedLabel}</span>.
              {Array.isArray(pendingUntag.nextTags) && pendingUntag.nextTags.length > 0
                ? ' It will remain tagged to its other sections.'
                : ' It will have no criteria tags until you tag it again.'}
            </p>
            {/* Grow with the note up to ~12 lines, then scroll — same pattern as
                the Criterion Mapping untag dialog so long notes are readable. */}
            <div className="text-sm text-gray-500 italic bg-gray-50 border border-gray-100 rounded-lg p-3 mb-5 max-h-48 overflow-y-auto">
              <p className="whitespace-pre-wrap break-words">
                “{pendingUntag.note.text}”
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingUntag(null)}
                className="py-2 px-4 rounded-md shadow-sm text-gray-900 border border-gray-300 hover:bg-gray-50 transition-all duration-200 font-semibold text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmUntag}
                className="py-2 px-4 rounded-md shadow-md bg-primary text-white hover:bg-primary/90 transition-all duration-200 font-semibold text-sm"
              >
                Remove tag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  SensoryPanel — DSM-5 Criterion B4 sensory reactivity assessment
//
//  Three-part layout mirroring the SSI criterion panel:
//    Left (sticky)  — Probing questions accordion (8 sensory domains)
//    Right top      — B4 evidence decision buttons
//    Right bottom   — StructuredNotesEditor for clinician notes
//
//  The evidence decision is stored as `dsm_B4_evidence` so the Observation
//  Review and MDT Review can pick it up alongside task-level evidence.
// ─────────────────────────────────────────────────────────────────────────

function SensoryPanel({ data, onFieldChange, readOnly = false, moveTargets = null, onMoveNote = null, currentMoveId = null, taggingEnabled = false }) {
  const val = useCallback(
    (fieldId) => (data || {})[fieldId],
    [data],
  );

  // ── Accordion state — one group open at a time ──────────────────────
  const [expandedGroup, setExpandedGroup] = useState(SENSORY_CATEGORIES[0]?.id || null);
  const toggleGroup = useCallback((id) => {
    setExpandedGroup(prev => (prev === id ? null : id));
  }, []);

  // ── Discussed questions ─────────────────────────────────────────────
  const discussedSet = useMemo(
    () => new Set(val('asked_questions') || []),
    [val],
  );

  const toggleDiscussed = useCallback((questionText) => {
    if (readOnly) return;
    const current = val('asked_questions') || [];
    const next = current.includes(questionText)
      ? current.filter(q => q !== questionText)
      : [...current, questionText];
    onFieldChange('asked_questions', next);
  }, [onFieldChange, val, readOnly]);

  const [showDiscussedPopup, setShowDiscussedPopup] = useState(false);
  const discussedArray = useMemo(() => val('asked_questions') || [], [val]);

  // ── Evidence decision (B4) ──────────────────────────────────────────
  const b4Evidence = val('dsm_B4_evidence') || null;

  // ── Notes ───────────────────────────────────────────────────────────
  const notes = useMemo(() => {
    const raw = val('notes');
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) return [{ id: Date.now(), text: raw.trim(), type: 'note' }];
    return [];
  }, [val]);

  const handleNotesChange = useCallback(
    (newNotes) => { if (!readOnly) onFieldChange('notes', newNotes); },
    [onFieldChange, readOnly],
  );

  return (
    <div>
      {/* Page heading — matches the task page h1 style for visual parity */}
      <h1 className="text-gray-900 text-3xl font-bold text-left mb-6">B4. Sensory Reactivity</h1>

      <div className="flex gap-6 items-start">
      {/* ── Left column: probing questions ──────────────────────────── */}
      <div className="w-80 flex-shrink-0 sticky top-0">
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 text-left">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-bold text-gray-800">Probing Questions</h3>

            {discussedArray.length > 0 && (
              <button
                onClick={() => setShowDiscussedPopup(true)}
                className="p-1 rounded-full text-gray-400
                  hover:text-primary hover:bg-primary/10
                  transition-colors duration-200"
                title="View questions marked as discussed"
              >
                <SearchIcon className="w-3.5 h-3.5" />
              </button>
            )}

            {discussedArray.length > 0 && (
              <span className="ml-auto text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                {discussedArray.length} discussed
              </span>
            )}
          </div>

          <p className="text-[10px] text-gray-400 mb-3 leading-relaxed">
            Click to mark as discussed. Drag into the notes editor to create a note from a question.
          </p>

          <div className="flex flex-col gap-0.5">
            {SENSORY_CATEGORIES.map((group) => (
              <CollapsibleQuestionGroup
                key={group.id}
                group={group}
                isExpanded={expandedGroup === group.id}
                onToggle={() => toggleGroup(group.id)}
                discussedSet={discussedSet}
                onToggleDiscussed={toggleDiscussed}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Right column: DSM Criteria Contribution table (B4 only) ── */}
      <div className="flex-1 min-w-0">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-primary text-white">
                <th className="px-4 py-3 text-sm font-bold text-left" style={{ width: '30%' }}>DSM Criteria</th>
                <th className="px-4 py-3 text-sm font-bold text-left" style={{ width: '140px' }}>Threshold met</th>
                <th className="px-4 py-3 text-sm font-bold text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const domain = DSM5_DOMAINS.find(d => d.id === 'B4');
                if (!domain) return null;
                const rowBg = b4Evidence === 'No evidence' ? 'bg-emerald-50'
                  : b4Evidence === 'Partial evidence' ? 'bg-amber-50'
                  : b4Evidence === 'Clear evidence' ? 'bg-red-50'
                  : b4Evidence === 'Inconclusive' ? 'bg-gray-100'
                  : 'bg-gray-50';
                return (
                  <tr className={`border-b border-gray-200 last:border-b-0 transition-colors duration-300 ${rowBg}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-start gap-1.5">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-bold leading-snug">
                            {domain.id}. {domain.shortTitle}
                          </p>
                          <p className="text-xs text-gray-500 leading-snug mt-0.5">
                            {domain.title}
                          </p>
                        </div>
                        <InfoTooltip
                          content={domain.tooltip}
                          label="B4 criteria detail"
                          maxWidth="300px"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1.5">
                        {DSM5_EVIDENCE_OPTIONS.map(opt => {
                          const isSelected = b4Evidence === opt;
                          const colours = EVIDENCE_COLOURS[opt];
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={readOnly ? undefined : () => onFieldChange('dsm_B4_evidence', isSelected ? null : opt)}
                              disabled={readOnly}
                              className={`
                                px-2.5 py-1 rounded-lg text-xs font-semibold border
                                transition-all duration-150 whitespace-nowrap
                                ${readOnly ? 'cursor-default' : ''}
                                ${isSelected
                                  ? `${colours.selectedBg} text-white ${colours.selectedBorder} shadow-sm`
                                  : `bg-white text-gray-600 border-gray-300 ${readOnly ? '' : colours.hoverBorder}`}
                              `}
                              aria-pressed={isSelected}
                              aria-label={`${opt} for B4`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StructuredNotesEditor
                        notes={notes}
                        onChange={readOnly ? undefined : handleNotesChange}
                        placeholder="Notes for B4…"
                        readOnlyPlaceholder="No notes recorded"
                        readOnly={readOnly}
                        minHeight="7.5rem"
                        domainId="B4"
                        moveTargets={moveTargets}
                        currentMoveId={currentMoveId}
                        onMoveNoteTo={readOnly ? undefined : onMoveNote}
                        enableCriteriaTagging={taggingEnabled && !readOnly}
                        defaultCriteriaTags={['B4']}
                      />
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      </div>

      {/* ── Discussed questions popup ──────────────────────────────── */}
      {showDiscussedPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setShowDiscussedPopup(false)}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 text-left animate-[fadeScaleIn_200ms_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowDiscussedPopup(false)}
              className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-4 h-4 text-gray-400" />
            </button>

            <h3 className="text-base font-bold text-gray-900 mb-1">Sensory (B4) — Questions</h3>
            <p className="text-xs text-gray-500 mb-4">
              Questions Discussed ({discussedArray.length})
            </p>

            <div className="flex flex-col gap-1.5 max-h-96 overflow-y-auto">
              {discussedArray.map((q, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2 rounded-lg text-xs italic leading-relaxed bg-blue-50 text-blue-700 border border-blue-200"
                >
                  &ldquo;{q}&rdquo;
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
